import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useAppStore from '../../store/index';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

interface ItBzLohnberechnungInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PDF_EXTRA_CSS = `
  .itbz-pdf-grid {
    display: table;
    width: 100%;
    margin: 10px 0 14px;
    border-collapse: separate;
    border-spacing: 8px 0;
  }
  .itbz-pdf-col {
    display: table-cell;
    width: 33.33%;
    border: 1.5px solid #1f2937;
    background: #f9fafb;
    padding: 10px 8px;
    text-align: center;
    vertical-align: top;
  }
  .itbz-pdf-col-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    color: #4b5563;
    margin-bottom: 4px;
  }
  .itbz-pdf-col-val {
    font-size: 18px;
    font-weight: 900;
    color: #111827;
    margin-bottom: 2px;
  }
  .itbz-pdf-col-sub {
    font-size: 10px;
    color: #4b5563;
  }
  .itbz-formula-box {
    border: 1.5px solid #1f2937;
    background: #f8fafc;
    padding: 12px 14px;
    margin: 8px 0 12px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    line-height: 1.5;
  }
  .itbz-formula-step {
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #d1d5db;
  }
  .itbz-formula-step:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
  .itbz-example-table {
    width: 100%;
    border-collapse: collapse;
    margin: 10px 0 14px;
    font-size: 12px;
  }
  .itbz-example-table th, .itbz-example-table td {
    border: 1px solid #9ca3af;
    padding: 6px 8px;
    text-align: left;
  }
  .itbz-example-table th {
    background: #f3f4f6;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 10.5px;
  }
`;

export const ItBzLohnberechnungInfoModal: React.FC<ItBzLohnberechnungInfoModalProps> = ({
    isOpen,
    onClose,
}) => {
    const settings = useAppStore((state) => state.settings);
    const [isExportingPdf, setIsExportingPdf] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const itConfig = {
        ...(settings?.countries?.IT || {}),
        ...(settings?.countryOverrides?.IT || {}),
    };
    const azubiFaktor = Number(itConfig.ItBz_Faktor_Azubi) || 1.42;
    const teilzeitFaktor = Number(itConfig.ItBz_Faktor_Teilzeit) || 1.65;
    const vollzeitFaktor = Number(itConfig.ItBz_Faktor_Vollzeit) || 1.80;
    const schwelleStd = Number(itConfig.ItBz_Vollzeit_Stundenschwelle) || 30;
    const mwstIt = Number(itConfig.MwSt_Satz) || 22.0;

    const azubiPct = Math.round((azubiFaktor - 1) * 100);
    const teilzeitPct = Math.round((teilzeitFaktor - 1) * 100);
    const vollzeitPct = Math.round((vollzeitFaktor - 1) * 100);
    const fmtNum = (n: number, d = 2) =>
        n.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });

    const buildPdfHtml = (): string => {
        const todayStr = new Date().toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });

        return `
<div>
  <div class="pdf-letterhead">
    <h1>PreisExpert</h1>
    <h2>Systematik- &amp; Prüfdokumentation · Lohnsystem Südtirol / Italien (IT_BZ)</h2>
  </div>

  <p class="doc-kicker">Vorlage für Coaches, Salonunternehmer, Steuerberater (Consulenti del Lavoro) &amp; Rechtsprüfung · Stand: ${todayStr}</p>

  <h3>1. Ausgangslage Südtirol / Italien („Netto in busta“)</h3>
  <p>
    Im Südtiroler und italienischen Friseur- und Kosmetikhandwerk erfolgt die Gehaltsvereinbarung zwischen Arbeitgeber und Arbeitnehmer branchenüblich auf Basis des monatlichen Auszahlungsbetrags (<strong>„Netto in busta“</strong>). Ein deutsches Lohnsteuerklassensystem (Steuerklassen I–VI) existiert in Italien nicht.
  </p>
  <p>
    Durch die gesetzlichen und kollektivvertraglichen Bestandteile — insbesondere das <strong>13. und ggf. 14. Monatsgehalt</strong> (Tredicesima / Quattordicesima), die gesetzliche <strong>Abfertigung / Rückstellung (TFR – Trattamento di Fine Rapporto)</strong>, die Sozialversicherungsbeiträge (<strong>INPS</strong>), die Unfallversicherung (<strong>INAIL</strong>) sowie die Einkommensteuer (<strong>IRPEF</strong> inkl. regionaler/kommunaler Zuschläge) — liegen die realen monatlichen Arbeitgeber-Gesamtkosten (<strong>Costo Totale Aziendale</strong>) signifikant über dem monatlich ausgezahlten Nettolohn:
  </p>

  <div class="itbz-pdf-grid">
    <div class="itbz-pdf-col">
      <div class="itbz-pdf-col-title">Lehrling / Azubi</div>
      <div class="itbz-pdf-col-val">× ${fmtNum(azubiFaktor)}</div>
      <div class="itbz-pdf-col-sub">(+${azubiPct} % auf Netto in busta)</div>
    </div>
    <div class="itbz-pdf-col">
      <div class="itbz-pdf-col-title">Teilzeit (&lt; ${fmtNum(schwelleStd, 0)} Wo-Std.)</div>
      <div class="itbz-pdf-col-val">× ${fmtNum(teilzeitFaktor)}</div>
      <div class="itbz-pdf-col-sub">(+${teilzeitPct} % auf Netto in busta)</div>
    </div>
    <div class="itbz-pdf-col">
      <div class="itbz-pdf-col-title">Vollzeit (&ge; ${fmtNum(schwelleStd, 0)} Wo-Std.)</div>
      <div class="itbz-pdf-col-val">× ${fmtNum(vollzeitFaktor)}</div>
      <div class="itbz-pdf-col-sub">(+${vollzeitPct} % auf Netto in busta)</div>
    </div>
  </div>

  <h3>2. Kosten-Nenner im Salon &amp; Steuerberater-Override (Costo Totale Aziendale)</h3>
  <p>
    In die betriebswirtschaftliche Gesamtkostenrechnung des Salons (produktive Personalkosten, unproduktive Personalkosten sowie Azubi-/Assistenzkosten) fließt <strong>ausnahmslos das reale Costo Totale Aziendale</strong> (<code>totalPersonnelCost</code>) ein — niemals nur der Nettolohn. Damit ist sichergestellt, dass 13./14. Monatsgehalt, TFR, INPS, INAIL und IRPEF zu 100 % in den Salonkosten und im Minutenpreis gedeckt sind.
  </p>
  <p>
    <strong>Steuerberater-Override (Priorität 1):</strong> Liegt für einen Mitarbeiter der exakte monatliche Gesamtkostenwert (inkl. anteiligem 13./14. Monatsgehalt und TFR-Rückstellung) aus dem Lohnkostenspiegel des Wirtschaftsberaters / Lohnbüros (<em>Consulente del Lavoro</em>) vor, wird dieser direkt im Feld <strong>„StB Costo Override“</strong> (<code>costoAziendaleOverride</code>) eingetragen. Sobald dort ein Wert &gt; 0 € hinterlegt ist, übersteuert dieser den pauschalen Stufen-Multiplikator zu 100 %.
  </p>

  <h3>3. Der Südtiroler Lohnfaktor (Reiner Netto-Multiplikator ohne Äquivalenz-Brutto)</h3>
  <p>
    PreisExpert verzichtet bewusst auf die Konstruktion eines künstlichen „deutschen Äquivalenz-Bruttos“. Damit Saloninhaber, Coaches und Mitarbeiter in Mitarbeitergesprächen und auf den Leistungsseiten direkt mit der real verhandelten Größe (<strong>„Netto in busta“</strong>) arbeiten können, wird der betriebliche Lohnfaktor in Südtirol als <strong>reiner Netto-Multiplikator</strong> direkt auf das vereinbarte Nettogehalt angewendet.
  </p>
  <p>
    <strong>Mathematische Konsequenz:</strong> Da dieser Multiplikator aus dem reinen Nettolohn sowohl die gesamten italienischen Arbeitgeber-Zusatzkosten (+${azubiPct} % bis +${vollzeitPct} %), die Urlaubs-/Krankheitsfortzahlung, sämtliche Salon-Gemeinkosten, den Gewinnaufschlag als auch die italienische Mehrwertsteuer (IVA ${fmtNum(mwstIt, 1)} %) erwirtschaften muss, fällt der ausgewiesene <strong>Lohnfaktor auf Netto-Basis rechnerisch zwingend höher aus als in Deutschland</strong> (typischerweise <strong>5,0 bis 6,0 auf Netto</strong> statt 3,5 bis 4,0 auf deutsches Brutto). Der geforderte Euro-Sollumsatz am Kunden ist im Ergebnis identisch und finanzmathematisch exakt gedeckt.
  </p>

  <div class="print-break">
    <h3>4. Mathematische Formel-Kaskade (Prüfnachweis im Rechenkern)</h3>
    <div class="itbz-formula-box">
      <div class="itbz-formula-step">
        <strong>Schritt 1 — Monatliche Arbeitgeber-Gesamtkosten pro Mitarbeiter (Costo Totale Aziendale):</strong><br/>
        • Priorität 1 (StB-Override aktiv, wenn Override &gt; 0): <code>totalPersonnelCost = costoAziendaleOverride</code><br/>
        • Priorität 2 (Südtirol IT_BZ Stufen-Modell): <code>totalPersonnelCost = (netWage + Lohnzusatz + Bonus) × Stufenfaktor (${fmtNum(azubiFaktor)} | ${fmtNum(teilzeitFaktor)} | ${fmtNum(vollzeitFaktor)})</code><br/>
        • Priorität 3 (Standard DE/AT/CH): <code>totalPersonnelCost = (grossWage + Lohnzusatz + Bonus) × (1 + LNK% / 100)</code>
      </div>
      <div class="itbz-formula-step">
        <strong>Schritt 2 — Kalkulatorischer Salon-Sollumsatz (Netto):</strong><br/>
        <code>Sollumsatz_Salon_Netto = Gemeinkosten + &Sigma; totalPersonnelCost (Prod. MA + Azubi-Anteil) + Prod. Unternehmerlohn + Material + Geräte</code><br/>
        <code>Kalk_Soll_inkl_Fortzahlung = Sollumsatz_Salon_Netto + (&Sigma; totalPersonnelCost_Prod × Urlaubsausgleich%)</code>
      </div>
      <div class="itbz-formula-step">
        <strong>Schritt 3 — Betrieblicher Lohnfaktor des Salons:</strong><br/>
        <code>Lohnfaktor_Netto = Kalk_Soll_inkl_Fortzahlung / (&Sigma; totalPersonnelCost_Prod + Prod. Unternehmerlohn)</code><br/>
        <code>Lohnfaktor_Brutto = Lohnfaktor_Netto × (1 + IVA ${fmtNum(mwstIt, 1)} %)</code><br/>
        <code>Lohnfaktor_Aktiv = max(3,50 ; Lohnfaktor_Brutto × (1 + Sicherheitsfaktor%) ; Manuell)</code>
      </div>
      <div class="itbz-formula-step">
        <strong>Schritt 4 — Individueller Mitarbeiter-Sollumsatz (Brutto-Kundenumsatz pro Monat):</strong><br/>
        • In Deutschland (DE): <code>MA_Sollumsatz_Brutto = Vereinbartes Brutto (grossWage) × Lohnfaktor_Aktiv</code><br/>
        • In Südtirol (IT_BZ): <code>MA_Sollumsatz_Brutto = Vereinbartes Netto in busta (netWage) × Lohnfaktor_Aktiv</code>
      </div>
    </div>

    <h3>5. Praxis-Vergleichsbeispiel (Vollzeit-Mitarbeiterin · ≥ ${fmtNum(schwelleStd, 0)} Std./Woche)</h3>
    <table class="itbz-example-table">
      <thead>
        <tr>
          <th>Kennzahl / Rechenschritt</th>
          <th>Berechnung / Herleitung (IT_BZ)</th>
          <th>Beispielwert</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>1. Vereinbartes Monats-Netto („Netto in busta“)</strong></td>
          <td>Auszahlungsbetrag lt. Lohnvereinbarung (<code>netWage</code>)</td>
          <td><strong>1.800,00 €</strong></td>
        </tr>
        <tr>
          <td><strong>2. Reale AG-Gesamtkosten (Costo Totale Aziendale)</strong></td>
          <td><code>1.800,00 € × ${fmtNum(vollzeitFaktor)}</code> (+${vollzeitPct} % inkl. 13./14. Gehalt, TFR, INPS, INAIL, IRPEF) — oder exakter StB-Override</td>
          <td><strong>${fmtNum(1800 * vollzeitFaktor)} €</strong></td>
        </tr>
        <tr>
          <td><strong>3. Kosten-Einfluss im Salon</strong></td>
          <td>In die Salon-Kalkulation fließen die vollen <strong>${fmtNum(1800 * vollzeitFaktor)} €</strong> als Personalkosten ein</td>
          <td><strong>100 % Kostendeckung</strong></td>
        </tr>
        <tr>
          <td><strong>4. Umsatz-Ziel (Sollumsatz Brutto am Kunden)</strong></td>
          <td><code>1.800,00 € (Netto) × 5,50 (Beispiel Netto-Lohnfaktor)</code><br/><em>(Entspricht rechnerisch ca. Faktor 3,06 auf das Costo Aziendale von ${fmtNum(1800 * vollzeitFaktor)} €)</em></td>
          <td><strong>9.900,00 € Brutto</strong></td>
        </tr>
      </tbody>
    </table>

    <div class="doc-panel">
      <p style="margin-bottom:6px;"><strong>Zusammenfassung für die steuerliche &amp; arbeitsrechtliche Prüfung:</strong></p>
      <p style="margin-bottom:0;">
        Die Berechnungslogik bildet die Südtiroler Praxis („Netto in busta“) transparent ab, berücksichtigt sämtliche Lohnnebenkosten inkl. TFR sowie 13./14. Monatsgehalt über global steuerbare Multiplikatoren (oder den einzelvertraglichen Steuerberater-Override) und vermeidet fiktive Brutto-Rückrechnungen.
      </p>
    </div>
  </div>
</div>
        `.trim();
    };

    const handleDownloadPdf = async () => {
        if (isExportingPdf) return;
        setIsExportingPdf(true);
        try {
            await downloadHtmlAsPdf({
                html: buildPdfHtml(),
                filename: 'PreisExpert_Lohnsystem_Suedtirol_IT_BZ_Dokumentation.pdf',
                extraCss: PDF_EXTRA_CSS,
                scrubMode: 'minimal',
                splitPrintBreaks: true,
                fitSectionsToPage: true,
                letterheadOnBreaks: true,
            });
        } catch (err: any) {
            console.error('Fehler beim PDF-Export (IT_BZ Dokumentation):', err);
            alert('PDF konnte nicht erstellt werden: ' + (err?.message || 'Unbekannter Fehler'));
        } finally {
            setIsExportingPdf(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/85 backdrop-blur-sm p-3 sm:p-6"
            onClick={onClose}
        >
            <div
                className="relative flex flex-col w-full max-w-4xl max-h-[88vh] rounded-2xl bg-[#0f1115] border border-[#dcb68a]/40 text-slate-200 shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header (Sticky) */}
                <div className="shrink-0 flex items-start justify-between gap-4 border-b border-[#dcb68a]/25 bg-[#15171e] px-5 sm:px-6 py-4">
                    <div>
                        <span className="inline-block text-[10px] font-black uppercase tracking-[0.2em] text-[#dcb68a] bg-[#dcb68a]/10 border border-[#dcb68a]/30 rounded px-2.5 py-0.5 mb-1.5">
                            Coach- &amp; Wirtschaftsberater-Dokumentation · IT_BZ
                        </span>
                        <h2 className="text-base sm:text-lg md:text-xl font-black text-white tracking-tight">
                            Lohnberechnung &amp; Lohnfaktor Südtirol (IT_BZ) — Systematik &amp; Nachweis
                        </h2>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={handleDownloadPdf}
                            disabled={isExportingPdf}
                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-[#dcb68a] bg-[#dcb68a]/15 hover:bg-[#dcb68a] text-[#dcb68a] hover:text-black font-black text-xs uppercase tracking-wider transition-all disabled:opacity-50"
                            title="Als A4-PDF herunterladen (für Coaches & Steuerberater)"
                        >
                            <span>📄</span>
                            <span>{isExportingPdf ? 'Erstelle PDF…' : 'Als PDF speichern'}</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="shrink-0 px-3 h-9 rounded-lg border border-red-500/40 bg-red-950/40 text-red-200 hover:bg-red-600 hover:text-white hover:border-red-500 flex items-center justify-center gap-1.5 font-black text-xs uppercase tracking-wider transition-colors"
                            title="Modal schließen (ESC)"
                        >
                            <span className="text-base leading-none">×</span>
                            <span>Schließen</span>
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 p-5 sm:p-6 space-y-5 overflow-y-auto text-sm leading-relaxed">
                    {/* 1. Ausgangslage Südtirol */}
                    <section className="rounded-xl bg-[#15171e] border border-slate-800 p-5">
                        <h3 className="text-xs font-black uppercase tracking-widest text-[#dcb68a] mb-2 flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#dcb68a]/15 border border-[#dcb68a]/40 text-[#dcb68a] flex items-center justify-center text-[11px]">1</span>
                            Ausgangslage Südtirol („Netto in busta“)
                        </h3>
                        <p className="text-slate-300 mb-3">
                            In Südtirol (Italien) erfolgt die Gehaltsverhandlung im Handwerk branchenüblich auf Basis des monatlichen Auszahlungsbetrags (<strong className="text-white">„Netto in busta“</strong>) und ohne deutsches Steuerklassensystem.
                        </p>
                        <p className="text-slate-300 mb-4">
                            Durch das <strong className="text-white">13. und ggf. 14. Monatsgehalt</strong> (Tredicesima / Quattordicesima), die gesetzliche <strong className="text-white">Abfertigung (TFR – Trattamento di Fine Rapporto)</strong>, Sozialabgaben (<strong className="text-white">INPS</strong>), Unfallversicherung (<strong className="text-white">INAIL</strong>) sowie Lohnsteuer (<strong className="text-white">IRPEF</strong>) liegen die realen monatlichen Arbeitgeber-Gesamtkosten (<strong className="text-[#dcb68a]">Costo Totale Aziendale</strong>) signifikant über dem ausgezahlten Monats-Netto:
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="rounded-lg bg-[#0f1115] border border-[#dcb68a]/25 p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Lehrling / Azubi</div>
                                <div className="text-xl font-mono font-black text-[#dcb68a] mt-1">× {fmtNum(azubiFaktor)}</div>
                                <div className="text-[11px] text-slate-400 mt-0.5">(+{azubiPct} % auf Netto)</div>
                            </div>
                            <div className="rounded-lg bg-[#0f1115] border border-[#dcb68a]/25 p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Teilzeit (&lt; {fmtNum(schwelleStd, 0)} Wo-Std.)</div>
                                <div className="text-xl font-mono font-black text-[#dcb68a] mt-1">× {fmtNum(teilzeitFaktor)}</div>
                                <div className="text-[11px] text-slate-400 mt-0.5">(+{teilzeitPct} % auf Netto)</div>
                            </div>
                            <div className="rounded-lg bg-[#0f1115] border border-[#dcb68a]/25 p-3 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Vollzeit (≥ {fmtNum(schwelleStd, 0)} Wo-Std.)</div>
                                <div className="text-xl font-mono font-black text-[#dcb68a] mt-1">× {fmtNum(vollzeitFaktor)}</div>
                                <div className="text-[11px] text-slate-400 mt-0.5">(+{vollzeitPct} % auf Netto)</div>
                            </div>
                        </div>
                    </section>

                    {/* 2. Kosten-Nenner im Salon */}
                    <section className="rounded-xl bg-[#15171e] border border-slate-800 p-5">
                        <h3 className="text-xs font-black uppercase tracking-widest text-[#dcb68a] mb-2 flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#dcb68a]/15 border border-[#dcb68a]/40 text-[#dcb68a] flex items-center justify-center text-[11px]">2</span>
                            Kosten-Nenner im Salon (Costo Totale Aziendale)
                        </h3>
                        <p className="text-slate-300">
                            In die betrieblichen Salonkosten (produktive Personalkosten, unproduktive Personalkosten und Azubi-Kosten) fließt <strong className="text-white">ausnahmslos das reale Costo Totale Aziendale</strong> (<code className="text-[#dcb68a] font-mono">totalPersonnelCost</code>) ein — niemals nur das Netto.
                        </p>
                        <p className="text-slate-300 mt-2">
                            <strong className="text-[#dcb68a]">Steuerberater-Override (Prio 1):</strong> Liegt der exakte monatliche Gesamtkostenwert (inkl. 13./14. Gehalt &amp; TFR-Rückstellung) aus dem Lohnkostenspiegel des Wirtschaftsberaters / Lohnbüros vor, kann dieser direkt als <strong className="text-white">Costo Aziendale Override</strong> eingetragen werden und übersteuert den Stufen-Faktor zu 100 %.
                        </p>
                    </section>

                    {/* 3. Der Südtiroler Lohnfaktor */}
                    <section className="rounded-xl bg-[#15171e] border border-slate-800 p-5">
                        <h3 className="text-xs font-black uppercase tracking-widest text-[#dcb68a] mb-2 flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#dcb68a]/15 border border-[#dcb68a]/40 text-[#dcb68a] flex items-center justify-center text-[11px]">3</span>
                            Der Südtiroler Lohnfaktor (Reiner Netto-Multiplikator)
                        </h3>
                        <p className="text-slate-300">
                            PreisExpert erfindet <strong className="text-white">kein künstliches „Äquivalenz-Brutto“</strong>. Damit der Saloninhaber und der Mitarbeiter auf der Leistungsseite direkt mit dem verhandelten <strong className="text-white">Netto in busta</strong> arbeiten können, wird der Lohnfaktor als direkter Hebel auf das Nettogehalt angewendet.
                        </p>
                        <p className="text-slate-300 mt-2">
                            Da dieser Multiplikator aus dem reinen Nettolohn sowohl die gesamten italienischen Lohnnebenkosten (+{azubiPct} % bis +{vollzeitPct} %), die Urlaubs-/Krankheitsfortzahlung, die Salon-Gemeinkosten als auch die Mehrwertsteuer ({fmtNum(mwstIt, 1)} %) erwirtschaften muss, liegt der <strong className="text-[#dcb68a]">effektive Ziel-Multiplikator auf Netto-Basis</strong> mathematisch zwingend höher als in Deutschland (typischerweise <strong className="text-white">5,0 bis 6,0</strong> auf Netto statt 3,5 bis 4,0 auf deutsches Brutto).
                        </p>
                    </section>

                    {/* 4. Mathematische Formel-Kaskade */}
                    <section className="rounded-xl bg-[#15171e] border border-[#dcb68a]/30 p-5">
                        <h3 className="text-xs font-black uppercase tracking-widest text-[#dcb68a] mb-3 flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#dcb68a]/15 border border-[#dcb68a]/40 text-[#dcb68a] flex items-center justify-center text-[11px]">4</span>
                            Formel-Kaskade im Detail (Prüfnachweis)
                        </h3>
                        <div className="space-y-2.5 font-mono text-xs bg-[#08090b] border border-slate-800 rounded-xl p-4 text-slate-300">
                            <div>
                                <span className="text-[#dcb68a] font-bold">Schritt 1 — Arbeitgeber-Gesamtkosten pro MA (Costo Aziendale):</span>
                                <div className="pl-3 text-slate-400 mt-0.5">
                                    • Prio 1 (Override aktiv): <span className="text-white">totalPersonnelCost = costoAziendaleOverride</span><br />
                                    • Prio 2 (IT_BZ Stufen): <span className="text-white">totalPersonnelCost = netWage × Faktor ({fmtNum(azubiFaktor)} | {fmtNum(teilzeitFaktor)} | {fmtNum(vollzeitFaktor)})</span><br />
                                    • Prio 3 (DE Standard): <span className="text-white">totalPersonnelCost = grossWage × (1 + LNK% / 100)</span>
                                </div>
                            </div>
                            <div className="border-t border-slate-800/80 pt-2">
                                <span className="text-[#dcb68a] font-bold">Schritt 2 — Kalkulatorischer Salon-Sollumsatz (Netto):</span>
                                <div className="pl-3 text-slate-400 mt-0.5">
                                    <span className="text-white">Sollumsatz_Salon = Gemeinkosten + Σ totalPersonnelCost (Prod. MA + Azubi-Anteil) + Prod. Unternehmerlohn + Material + Geräte</span><br />
                                    <span className="text-white">Kalk_Soll_inkl_Fortzahlung = Sollumsatz_Salon + (Σ totalPersonnelCost_Prod × Urlaubsausgleich%)</span>
                                </div>
                            </div>
                            <div className="border-t border-slate-800/80 pt-2">
                                <span className="text-[#dcb68a] font-bold">Schritt 3 — Betrieblicher Lohnfaktor:</span>
                                <div className="pl-3 text-slate-400 mt-0.5">
                                    <span className="text-white">Lohnfaktor_Netto = Kalk_Soll_inkl_Fortzahlung / (Σ totalPersonnelCost_Prod + Prod. Unternehmerlohn)</span><br />
                                    <span className="text-white">Lohnfaktor_Brutto = Lohnfaktor_Netto × (1 + MwSt% [{fmtNum(mwstIt, 1)} % in IT])</span><br />
                                    <span className="text-white">Lohnfaktor_Aktiv = max(3,50 ; Lohnfaktor_Brutto × (1 + Sicherheitsfaktor%) ; Manuell)</span>
                                </div>
                            </div>
                            <div className="border-t border-slate-800/80 pt-2">
                                <span className="text-[#dcb68a] font-bold">Schritt 4 — Mitarbeiter-Sollumsatz (Brutto am Kunden):</span>
                                <div className="pl-3 text-slate-400 mt-0.5">
                                    • In Deutschland (DE): <span className="text-white">MA_Sollumsatz = Bruttolohn (grossWage) × Lohnfaktor_Aktiv</span><br />
                                    • In Südtirol (IT_BZ): <span className="text-white">MA_Sollumsatz = Netto in busta (netWage) × Lohnfaktor_Aktiv</span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Footer (Sticky) */}
                <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 border-t border-slate-800 bg-[#15171e] px-5 sm:px-6 py-3.5">
                    <button
                        type="button"
                        onClick={handleDownloadPdf}
                        disabled={isExportingPdf}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-[#dcb68a]/60 bg-[#0f1115] hover:bg-[#dcb68a]/15 text-[#dcb68a] font-black text-xs uppercase tracking-widest transition-all disabled:opacity-50"
                    >
                        <span>📄</span>
                        <span>{isExportingPdf ? 'Erstelle PDF-Dokument…' : 'Als PDF speichern (für Coaches / StB)'}</span>
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-lg bg-[#dcb68a] hover:bg-[#c9a375] text-black font-black text-xs uppercase tracking-widest transition-colors shadow-lg"
                    >
                        Verstanden &amp; Schließen
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ItBzLohnberechnungInfoModal;
